<template>
  <v-card>
    <v-card-title class="primary white--text">
      {{ $t('header.iniform') }}
    </v-card-title>
    <v-card-text>
      <v-file-input
        v-model="ini_file"
        show-size
        accept=".ini"
        :label="$t('label.ini_file')"
        :hint="$t('hint.ini_file')"
        persistent-hint
        @change="loadIniFromFile"
        ref="inifile"
      ></v-file-input>
    </v-card-text>
    <v-divider></v-divider>
    <v-stepper v-model="iniFormStep" alt-labels non-linear flat>
      <v-stepper-header>
        <v-stepper-step
          step="1"
          :complete="iniFormStep > 1"
          :rules="[() => this.forms.desc]"
          editable
        >
          {{ $t('form.ini.step.filedesc') }}
        </v-stepper-step>
        <v-divider/>
        <v-stepper-step
          step="2"
          :complete="iniFormStep > 2"
          :rules="[() => this.forms.editions]"
          editable
        >
          {{ $t('form.ini.step.editions') }}
        </v-stepper-step>
        <v-divider/>
        <v-stepper-step
          step="3"
          :complete="iniFormStep > 3"
          editable
        >
          {{ $t('form.ini.step.options') }}
        </v-stepper-step>
        <v-divider/>
        <v-stepper-step
          step="4"
          :complete="iniFormStep > 4"
          :rules="[() => this.forms.file]"
          editable
        >
          {{ $t('form.ini.step.file') }}
        </v-stepper-step>
      </v-stepper-header>
      <v-stepper-items>
        <v-stepper-content step="1">
          <v-form
            v-model="forms.desc"
            ref="desc"
            lazy-validation
          >
            <v-text-field
              v-model="ini.title"
              hint=""
              :label="$t('label.title')"
              :rules="rules.required"
              class="field-required"
            ></v-text-field>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="ini.editor.name"
                  hint=""
                  :label="$t('label.editor')"
                  :rules="rules.required"
                  class="field-required"
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="ini.editor.mail"
                  hint=""
                  :label="$t('label.editor_mail')"
                  :rules="rules.isEmail"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-text-field
              v-model="ini.publisher.name"
              hint=""
              :label="$t('label.publisher')"
              :rules="rules.required"
              class="field-required"
            ></v-text-field>
            <v-text-field
              v-model="ini.publisher.url"
              hint=""
              :label="$t('label.publisher_url')"
              :rules="rules.isURL"
            ></v-text-field>
          </v-form>
          <v-btn
            color="primary"
            @click="iniFormStep = 2; validateForm('desc')"
          >
            {{ $t('Continue') }}
          </v-btn>
        </v-stepper-content>
        <v-stepper-content step="2">
          <v-form
            v-model="forms.editions"
            ref="editions"
            lazy-validation
          >
            <template v-for="(edition, idx) in ini.editions">
              <v-card
                :key="idx"
                outlined
                class="mb-8"
              >
                <v-card-text>
                  <v-row dense>
                    <v-col cols="2">
                      <v-text-field
                        v-model="edition.id"
                        hint=""
                        :label="$t('label.id')"
                        :rules="rules.uniqueEditionIds"
                        class="field-required"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="10">
                      <v-text-field
                        v-model="edition.title"
                        hint=""
                        :label="$t('label.title')"
                        :rules="rules.required"
                        class="field-required"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="edition.type"
                        :label="$t('label.type')"
                        row
                        mandatory
                      >
                        <v-radio
                          v-for="type in edition_types"
                          :key="type"
                          :label="type"
                          :value="type"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-btn
                  fab
                  outlined
                  small
                  color="grey lighten-2"
                  @click="deleteEdition(idx)"
                  absolute
                  bottom
                  elevation="0"
                  class="btn-delete"
                >
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </v-card>
            </template>
          </v-form>
          <v-btn
            color="primary"
            @click="iniFormStep = 3; validateForm('editions')"
          >
            {{ $t('Continue') }}
          </v-btn>
          <v-btn
            @click="addEdition"
          >
            <v-icon small>mdi-plus</v-icon>{{ $t('add edition') }}
          </v-btn>
        </v-stepper-content>
        <v-stepper-content step="3">
          <v-form
            v-model="forms.options"
            ref="options"
          >
            <v-switch
              v-for="[key, value] in Object.entries(csvOptions)"
              :key="'opt-' + key"
              :label="$t('label.' + key) + ': ' + $t('value.' + value.toString())"
              v-model="csvOptions[key]"
              :hint="$t('hint.' + key)"
              persistent-hint
              :disabled="key === 'v'"
            ></v-switch>
          </v-form>
          <v-btn
            color="primary"
            @click="iniFormStep = 4"
          >
            {{ $t('Continue') }}
          </v-btn>
        </v-stepper-content>
        <v-stepper-content step="4">
          <v-form
            v-model="forms.file"
            ref="file"
            lazy-validation
          >
            <v-file-input
              v-model="csv_file"
              :label="$t('label.csv_file')"
              hint=""
              chips
              counter
              multiple
              show-size
              accept=".csv"
              :rules="rules.required"
              class="field-required"
            ></v-file-input>
            <v-text-field
              v-model="ini.extra_delimiter"
              hint=""
              :label="$t('label.delimiter')"
            ></v-text-field>
            <v-text-field
              v-model="ini.file_url"
              hint=""
              :label="$t('label.file_url')"
            ></v-text-field>
          </v-form>
          <v-btn
            color="primary"
            @click="submit"
            :disabled="!valid"
          >{{ $t('Submit') }}</v-btn>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-card>
</template>

<script>
import { parse } from '@/utils/ini'

export default {

  name: 'IniForm',

  data () {
    return {
      forms: {
        desc: false,
        editions: false,
        file: false,
        options: true
      },
      iniFormStep: 1,
      ini: {
        title: '',
        editor: {
          name: '',
          mail: ''
        },
        publisher: {
          name: '',
          url: ''
        },
        file_url: '',
        extra_delimiter: '',
        type: '',
        editions: []
      },
      ini_file: null,
      csv_file: null,
      csvOptions: {
        a: false,
        n: false,
        'line-numbers': false,
        v: false
      },
      jobs: [],
      edition_types: [
        'print',
        'online',
        'hybrid'
      ],
      rules: {
        required: [v => !!v || this.$t('rules.required')],
        notEmpty: [v => v.length > 0 || this.$t('rules.no_empty_editors')],
        uniqueEditionIds: [
          v => !!v || this.$t('rules.required'),
          v => this.uniqueIds.length === this.ini.editions.length || this.$t('rules.uniqueEditionIds')
        ],
        isEmail: [v => v === null || v.length === 0 || v.match(/^.+\@.+\..+/) !== null || this.$t('rules.no_valid_mail')],
        isURL: [v => v === null || v.length === 0 || v.match(/^https?:\/\/.+\..+/) !== null || this.$t('rules.no_valid_url')]
      }
    }
  },

  computed: {
    moreThanOneEdition () {
      return this.ini.editions.length > 1
    },
    valid () {
      return this.forms.desc && this.forms.editions && this.forms.file
    },
    uniqueIds () {
      const ids = []
      this.ini.editions.forEach(e => {
        if (ids.indexOf(e.id) === -1) {
          ids.push(e.id)
        }
      })
      return ids
    }
  },

  methods: {
    submit () {
      this.validateForm('desc')
      this.validateForm('editions')
      this.validateForm('file')
      if (this.valid) {
        const url = process.env.VUE_APP_ROOT_API + '/upload'
        const formData = new FormData()
        formData.append('ini_json', JSON.stringify(this.ini))
        if (this.csv_file && this.csv_file.length) {
          formData.append('csv_file', this.csv_file[0], this.csv_file[0].name)
        }
        if (this.csvOptions.a) { formData.append('a', this.csvOptions.a) }
        if (this.csvOptions.n) { formData.append('n', this.csvOptions.n) }
        if (this.csvOptions['line-numbers']) { formData.append('line-numbers', this.csvOptions['line-numbers']) }
        if (this.csvOptions.v) { formData.append('v', this.csvOptions.v) }
        this.axios.post(url, formData)
          .then(response => {
            const taskIni = Object.assign({}, this.ini)
            const taskOptions = Object.assign({}, this.csvOptions)
            const task = Object.assign(
              {
                '@id': response.data['@id'],
                id: response.data.id,
                ini: taskIni,
                csvOptions: taskOptions
              })
            this.$store.dispatch('addTask', task)
            // reset forms if all went successfully
            this.$refs.desc.reset()
            this.$refs.editions.reset()
            this.$refs.file.reset()
            Object.keys(this.csvOptions).forEach(k => { this.csvOptions[k] = false })
            this.iniFormStep = 1
            // multiple editions needs manual reset
            this.ini.editions.splice(0)
            // reset ini file
            this.$refs.inifile.reset()
          })
          .catch(error => {
            this.$log.error(error)
          })
      }
    },
    validateForm (ref) {
      this.forms[ref] = this.$refs[ref].validate()
    },
    addEdition () {
      const editionTemplate = { id: '', title: '', type: '' }
      this.ini.editions.push(editionTemplate)
    },
    deleteEdition (idx) {
      this.ini.editions.splice(idx, 1)
    },
    loadIniFromFile (file) {
      const _this = this
      const llog = _this.$log
      const lini = _this.ini
      llog.debug(file)

      if (file) {
        if (window.FileReader) {
          const reader = new FileReader()
          // when the file is read it triggers the onload event above.
          reader.readAsText(file, 'UTF-8')
          // Handle errors load
          reader.onload = function () {
            const iniObj = parse(reader.result)
            llog.debug(iniObj)
            for (const k in iniObj) {
              if (k === 'Project') {
                // Assign values to project specific config
                lini.title = iniObj[k].title ? iniObj[k].title : ''
                lini.editor.name = iniObj[k].editor ? iniObj[k].editor : ''
                lini.editor.mail = iniObj[k].editor_mail ? iniObj[k].editor_mail : ''
                lini.publisher.name = iniObj[k].publisher ? iniObj[k].publisher : ''
                lini.publisher.url = iniObj[k].publisher_url ? iniObj[k].publisher_url : ''
                if ('extra-delimiter' in iniObj[k]) {
                  lini.extra_delimiter = iniObj[k]['extra-delimiter']
                } else if ('extra_delimiter' in iniObj[k]) {
                  // the more pythonic version produced by webservice
                  lini.extra_delimiter = iniObj[k]['extra_delimiter']
                }
                if ('fileURL' in iniObj[k]) {
                  lini.file_url = iniObj[k]['fileURL']
                } else if ('file_url' in iniObj[k]) {
                  // the more pythonic version produced by webservice
                  lini.file_url = iniObj[k]['file_url']
                }
              } else if (lini.editions.findIndex(e => e.id === k) === -1) {
                // Add as edition, if not already specified (unique IDs)
                lini.editions.push({
                  id: k,
                  title: iniObj[k].title,
                  type: iniObj[k].type ? iniObj[k].type : ''
                })
              }
            }
            // Jump to last form step, as we already filled in the form
            _this.iniFormStep = 4
          }
        } else {
          llog.error('FileReader are not supported in this browser.')
        }
      } else {
        llog.debug('No input file chosen.')
      }
    }
  }
}
</script>

<style lang="css">
.field-required label:after {
  content: '*';
}
.btn-delete {
  background-color: #fff;
  margin: 0 auto;
  left: 0;
  right: 0;
}
</style>
